import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MessageCircle, FileCheck, BadgeDollarSign, Camera, Clock, FileText, BadgeCheck } from "lucide-react";
import { CTA, waInspectorHref } from "@/lib/business";
import { trackEvent } from "@/lib/analytics";

// Illustrative rows only — the headline count matches the approved sample PDF
// (88 findings: 25 Low / 52 Medium / 11 High).
const findings = [
  { cat: "Water ingress — ceiling", loc: "Bathroom 2", sev: "High", cls: "bg-[#E11D2E] text-white border-[#E11D2E]", dot: "#E11D2E" },
  { cat: "AC airflow inconsistency", loc: "Master", sev: "Medium", cls: "bg-[#FF6A00] text-white border-[#FF6A00]", dot: "#FF6A00" },
  { cat: "Silicone finish defect", loc: "Kitchen", sev: "Medium", cls: "bg-[#FF6A00] text-white border-[#FF6A00]", dot: "#FF6A00" },
  { cat: "Door alignment", loc: "Bedroom 3", sev: "Low", cls: "bg-[#22A35A] text-white border-[#22A35A]", dot: "#22A35A" },
];

const trust = [
  ["Independent from developers and contractors", ShieldCheck],
  ["Fixed fee confirmed before booking", BadgeDollarSign],
  ["Photographic findings with clear priorities", Camera],
  ["Report delivery within your confirmed timeframe", Clock],
];

export default function Hero({ onQuote }) {
  return (
    <section id="top" className="relative pt-6 sm:pt-8 lg:pt-10 pb-16 lg:pb-20 bg-gradient-to-b from-white to-soft" data-testid="hero">
      <div className="wrap grid lg:grid-cols-[1.05fr_.95fr] gap-10 lg:gap-16 items-center">
        <div>
          <span className="eyebrow block">Independent property inspection · UAE</span>
          <h1 className="text-[clamp(2.1rem,4.3vw,3.35rem)] leading-[1.05] tracking-[-0.035em] mt-3 mb-4">
            Independent Property Snagging and Inspections in <span className="text-blue">Dubai</span>
          </h1>
          <p className="text-[1.08rem] text-bodytext max-w-[52ch] mb-7">
            Inspect before you accept. Fedari helps owners, buyers and investors understand a property&apos;s visible condition before handover or purchase. Get clear photographic findings, severity ratings and recommended next actions from an independent team working for you.
          </p>
          <div className="mb-7 flex w-fit max-w-full items-center gap-3.5 bg-ink text-white rounded-xl pl-2.5 pr-5 py-2.5 shadow-[0_10px_28px_rgba(17,24,39,.22)]" data-testid="hero-price-cert-banner">
            <span className="w-10 h-10 rounded-lg bg-blue grid place-items-center flex-none shadow-[0_2px_8px_rgba(10,108,255,.45)]">
              <BadgeCheck size={21} className="text-white" />
            </span>
            <span className="font-display text-[1rem] sm:text-[1.15rem] tracking-tight leading-snug" style={{ color: "#fff" }}>
              From AED 799 <span className="text-[#7FB2FF] px-0.5">·</span> <span className="text-[#7FB2FF]">InterNACHI® Certified Professional Inspector</span>
            </span>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-8">
            <button onClick={() => { trackEvent("click_primary_cta", { location: "hero" }); trackEvent("homepage_quote_click", { location: "hero" }); onQuote(); }} data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 hover:-translate-y-px transition-all shadow-[0_1px_2px_rgba(10,108,255,.24)]">
              {CTA.primary} <ArrowRight size={17} />
            </button>
            <Link to="/sample-snagging-report" onClick={() => trackEvent("sample_report_click", { location: "hero" })} data-testid="hero-sample-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-blue hover:text-blue transition-colors">
              <FileText size={17} /> View a Sample Report
            </Link>
            <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" onClick={() => { trackEvent("whatsapp_click", { location: "hero" }); trackEvent("homepage_whatsapp_click", { location: "hero" }); }} data-testid="hero-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-wa hover:text-wa transition-colors">
              <MessageCircle size={17} /> {CTA.whatsapp}
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-6 border-t border-line" data-testid="hero-trust-points">
            {trust.map(([t, Icon]) => (
              <div key={t} className="flex items-center gap-2 text-[0.85rem] font-medium text-bodytext">
                <Icon size={16} className="text-blue flex-none" /> {t}
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-2 lg:mt-0 mb-6 lg:mb-0">
          <div className="rounded-[20px] overflow-hidden border border-line shadow-md aspect-[16/10]">
            <picture>
              <source type="image/avif" srcSet="/inspector-2-600.avif 600w, /inspector-2-900.avif 900w" sizes="(max-width: 1024px) 100vw, 50vw" />
              <source type="image/webp" srcSet="/inspector-2-600.webp 600w, /inspector-2-900.webp 900w" sizes="(max-width: 1024px) 100vw, 50vw" />
              <img src="/inspector-2-900.webp" alt="Illustrative inspection scene"
                width={900} height={563} loading="eager" fetchPriority="high" decoding="async"
                className="w-full h-full object-cover" data-testid="hero-inspector-photo" />
            </picture>
          </div>
          <Link to="/sample-snagging-report" data-testid="hero-sample-card" className="block bg-white border border-line rounded-[20px] p-5 sm:p-6 shadow-lg relative overflow-hidden -mt-14 mx-auto w-[94%] hover:border-blue transition-colors">
            <div className="flex justify-between items-start mb-5 pb-5 border-b border-line">
              <div>
                <div className="font-mono-jb text-[0.6rem] tracking-[0.16em] text-blue uppercase">Sample report</div>
                <div className="font-display text-[1.2rem] mt-1.5">Sample inspection report</div>
              </div>
              <div className="text-right">
                <div className="font-display text-[2.1rem] leading-none">88</div>
                <div className="font-mono-jb text-[0.6rem] text-muted uppercase tracking-widest mt-1">Findings</div>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              {findings.map((f, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-line rounded-[10px] px-3.5 py-2.5">
                  <span className="w-2 h-2 rounded-full flex-none" style={{ background: f.dot }} />
                  <span className="text-[0.83rem] font-semibold flex-1 text-ink">{f.cat}</span>
                  <span className="font-mono-jb text-[0.66rem] text-muted hidden sm:block">{f.loc}</span>
                  <span className={`font-mono-jb text-[0.62rem] tracking-wide uppercase px-1.5 py-1 rounded border ${f.cls}`}>{f.sev}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-[0.66rem] text-muted font-mono-jb tracking-wide uppercase">Findings shown are illustrative · 88 findings in the approved sample</div>
          </Link>
          <div className="absolute -bottom-4 left-2 sm:-left-4 bg-white border border-line rounded-xl px-3.5 py-2.5 shadow-md flex gap-3 items-center">
            <div className="w-9 h-9 rounded-lg bg-blue-50 grid place-items-center text-blue flex-none"><FileCheck size={18} /></div>
            <div className="text-[0.68rem] text-muted leading-tight">
              <b className="block text-ink text-[0.84rem]">Low · Medium · High</b>
              Clear severity ratings
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
